import type { AttendanceRecord, Holiday, LeaveDay, WfhDay } from "@/api/types";
import { HALF_DAY_MINUTES, currentWorkDate, orgTodayStr, orgYear } from "./org-time";

/**
 * Direct port of hrms-backend/src/utils/calendar.js. The rules are duplicated
 * rather than fetched because the frontend has to classify every cell of a
 * month grid, and a round trip per day would be absurd. If the backend's rules
 * change, this file changes in the same commit.
 */

/**
 * Today, as the backend means it: the current WORK day on the org's clock — the
 * same value it writes into attendance.date and attendance_events.work_date.
 *
 * Two things folded in here, both of which used to be wrong:
 *
 *   * the org's clock, not the browser's. Every screen that looks a record up by
 *     "today" (HR's roster does exactly that, `record.date === today`) was
 *     searching a date the backend may never have written.
 *   * the 6am boundary. Between midnight and 06:00 this is yesterday's date,
 *     because the shift running then belongs to yesterday.
 *
 * See lib/org-time.ts.
 */
export function todayStr(): string {
  return currentWorkDate();
}

/**
 * Today as a PERSON would name it: the org's calendar date, with no 6am rule.
 *
 * The distinction matters because the backend has only this one. Its own
 * todayStr() is the plain org date (utils/timezone.js), so between midnight and
 * 06:00 IST every date the two sides validate against differed by a day: the
 * work-from-home picker offered a minimum date the server then rejected as being
 * in the past, and the backfill form refused yesterday — a day the server would
 * have accepted — saying it had not passed yet.
 *
 * Rule of thumb: attributing a PUNCH to a shift uses todayStr() above; asking
 * somebody to pick a DATE, and checking the date they picked, uses this one.
 */
export function calendarTodayStr(): string {
  return orgTodayStr();
}

export function toDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function parseDateStr(value: string): Date {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

/** Sundays, the 2nd and 4th Saturday, and any fixed company holiday. */
export function isRestDay(dateStr: string, holidaySet?: Set<string>): boolean {
  const date = parseDateStr(dateStr);
  const day = date.getDay();
  if (day === 0) return true;
  if (day === 6) {
    const nth = Math.ceil(date.getDate() / 7);
    if (nth === 2 || nth === 4) return true;
  }
  return holidaySet?.has(dateStr) ?? false;
}

/**
 * Moves a YYYY-MM-DD date by whole days, staying in date-string space.
 *
 * Every screen that asks for "the last 14 days" builds its window from this
 * rather than from `new Date()`, so the window is anchored to the org's today
 * (todayStr) and not to the viewer's — the same reason todayStr itself is pinned.
 */
export function shiftDate(dateStr: string, days: number): string {
  const date = parseDateStr(dateStr);
  date.setDate(date.getDate() + days);
  return toDateStr(date);
}

export function eachDateInRange(startStr: string, endStr: string): string[] {
  const dates: string[] = [];
  const cursor = parseDateStr(startStr);
  const end = parseDateStr(endStr);
  while (cursor <= end) {
    dates.push(toDateStr(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

export type DayStatus = "complete" | "half" | "inactive" | "absent";

/**
 * One rule, every mode: a day is worth what its hours come to.
 *
 *   4.5h or more   complete  "Full day"
 *   under 4.5h     half      "Half day"
 *   no sessions    absent    "No record"  (or "inactive" if the day was declared off)
 *
 * `total_minutes` is the sum of that day's CLOSED sessions, so several sessions
 * add up and a day someone never closed contributes nothing — which lands it
 * under 4.5h and makes it a half day. The arrival is known and the departure is
 * not, so the day is credited at half rather than guessed at or thrown away.
 *
 * Onsite and remote are judged identically. Remote used to be exempt from hours
 * altogether, which meant a remote day left open counted as a complete one.
 *
 * Direct port of hrms-backend/src/utils/calendar.js's dayStatus.
 */
export function dayStatus(
  record: Pick<AttendanceRecord, "check_in" | "total_minutes" | "status"> | null | undefined,
): DayStatus {
  if (!record) return "absent";
  // A DECLARED remote day is settled by what was declared, not by hours: it has
  // none, and the 4.5h rule would read every one of them as an absence. Hence
  // before the minutes checks, not after.
  if (record.status === "present") return "complete";
  if (record.status === "half") return "half";
  if ((record.total_minutes ?? 0) >= HALF_DAY_MINUTES) return "complete";
  // Any check-in at all means they were here, so the day is worth at least half.
  if (record.check_in) return "half";
  // No check-in: either a deliberate "not working today" or nothing at all.
  if (record.status === "inactive") return "inactive";
  return "absent";
}

/** What a single calendar cell means, once rest days and leave are accounted for. */
export type CellKind =
  | DayStatus
  | "rest"
  | "holiday"
  | "future"
  | "leave"
  | "leave-pending"
  | "wfh";

/**
 * Leave rows indexed for O(1) lookup, keyed `${user_id}|${date}` — the same key
 * shape the attendance-record maps beside them already use.
 */
export function leaveMap(leave: LeaveDay[] | undefined): Map<string, LeaveDay> {
  const map = new Map<string, LeaveDay>();
  for (const day of leave ?? []) map.set(`${day.user_id}|${day.date}`, day);
  return map;
}

/** The same, keyed by date alone, for the views that only ever show one person. */
export function leaveByDate(leave: LeaveDay[] | undefined): Map<string, LeaveDay> {
  const map = new Map<string, LeaveDay>();
  for (const day of leave ?? []) map.set(day.date, day);
  return map;
}

/** Work-from-home rows indexed `${user_id}|${date}`, like leaveMap. */
export function wfhMap(wfh: WfhDay[] | undefined): Map<string, WfhDay> {
  const map = new Map<string, WfhDay>();
  for (const day of wfh ?? []) map.set(`${day.user_id}|${day.date}`, day);
  return map;
}

/** The same, keyed by date alone, for single-person views. */
export function wfhByDate(wfh: WfhDay[] | undefined): Map<string, WfhDay> {
  const map = new Map<string, WfhDay>();
  for (const day of wfh ?? []) map.set(day.date, day);
  return map;
}

/**
 * What one cell of the tracker should say.
 *
 * The order of the branches IS the policy, so it is worth reading as one:
 *
 *   1. worked        A day with a real record wins over everything, leave
 *                    included. Someone who works a day they had booked off has
 *                    worked it, and payroll should see the hours; the leave
 *                    beside it is surfaced separately (see leaveClash) rather
 *                    than by hiding what they did.
 *   2. holiday       An org closure outranks an individual's leave. A leave span
 *                    that happens to cover Diwali does not turn Diwali into a
 *                    day that person spent leave on — the leave-day arithmetic
 *                    excludes it too, so this keeps the cell and the balance
 *                    telling the same story.
 *   3. weekly off    Same reasoning, for Sundays and 2nd/4th Saturdays.
 *   4. leave         Approved. This is the branch that fixes the old behaviour:
 *                    the day used to fall through to "absent", so people on
 *                    sanctioned leave read as absentees on every screen.
 *   5. leave-pending Applied for, not yet decided. Deliberately its own kind
 *                    rather than folded into 4 — "they asked" and "it was
 *                    granted" are different facts, and an approver looking at
 *                    the tracker is exactly who needs to tell them apart.
 *   6. wfh           An UPCOMING day approved to be worked from home. Two
 *                    deliberate limits on this branch:
 *
 *                    Only future days. A WFH day in the past, or today, with no
 *                    record on it is a day somebody did not clock — and painting
 *                    it "work from home" would hide exactly that, all day, on the
 *                    one screen a manager would notice it from. Once they check
 *                    in, branch 1 has it, and the day carries mode 'remote'.
 *
 *                    Only APPROVED. A pending request is a question. It lives on
 *                    the work-from-home tab, where it can be answered.
 *   7. future / absent  as before.
 */
export function classifyDay(
  dateStr: string,
  record: Pick<AttendanceRecord, "check_in" | "total_minutes" | "status"> | undefined,
  holidayNames: Map<string, string>,
  leave?: Pick<LeaveDay, "status"> | undefined,
  wfh?: Pick<WfhDay, "status"> | undefined,
  today = todayStr(),
): CellKind {
  const isHoliday = holidayNames.has(dateStr);
  const rest = isRestDay(dateStr, new Set(holidayNames.keys()));

  // A worked rest day still shows as worked — that is what earns comp off. A
  // declared day (remote full/half, or a deliberate day off) has a real row but
  // no check-in, so it needs its own branch here rather than falling through to
  // the generic rest/holiday/absent cases below.
  if (record?.check_in || (record?.status && record.status !== null)) return dayStatus(record);
  if (isHoliday) return "holiday";
  if (rest) return "rest";
  if (leave?.status === "approved") return "leave";
  if (leave?.status === "pending") return "leave-pending";
  if (wfh?.status === "approved" && dateStr > today) return "wfh";
  if (dateStr > today) return "future";
  return "absent";
}

/**
 * Leave that a day has a record IN SPITE of — the case branch 1 above lets
 * through silently. Returned so a screen can show the worked day AND flag that
 * it was booked as leave, which is the pair HR has to reconcile.
 */
export function leaveClash(
  record: Pick<AttendanceRecord, "check_in"> | undefined,
  leave: LeaveDay | undefined,
): LeaveDay | null {
  return record?.check_in && leave ? leave : null;
}

/** A day nobody should be counted absent for. */
export function isLeaveKind(kind: CellKind): boolean {
  return kind === "leave" || kind === "leave-pending";
}

export const CELL_LABEL: Record<CellKind, string> = {
  complete: "Full day",
  half: "Half day",
  inactive: "Day off",
  absent: "No record",
  rest: "Weekly off",
  holiday: "Company holiday",
  future: "Upcoming",
  leave: "On leave",
  "leave-pending": "Leave pending",
  // A day still to come, approved to be worked from home. Once it is worked it
  // reads as the full or half day it was, so this label only ever means "planned".
  wfh: "Work from home",
};

/** Monday-first weekday order, matching how the month grid is laid out. */
export const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

/** Index into WEEKDAY_LABELS for a given date (JS getDay is Sunday-first). */
export function weekdayIndex(dateStr: string): number {
  const day = parseDateStr(dateStr).getDay();
  return (day + 6) % 7;
}

/** Start (Monday) and end (Sunday) of the week containing `dateStr`. */
export function weekBounds(dateStr: string): { start: string; end: string } {
  const date = parseDateStr(dateStr);
  const offset = weekdayIndex(dateStr);
  const start = new Date(date);
  start.setDate(date.getDate() - offset);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { start: toDateStr(start), end: toDateStr(end) };
}

export function monthBounds(month: string): { start: string; end: string } {
  const [y, m] = month.split("-").map(Number);
  // Only reached for a malformed month string, but the org's clock is the one
  // this module works in — the viewer's is never the right answer here.
  const year = y ?? orgYear();
  const monthIndex = (m ?? 1) - 1;
  return {
    start: toDateStr(new Date(year, monthIndex, 1)),
    end: toDateStr(new Date(year, monthIndex + 1, 0)),
  };
}

export function currentMonth(): string {
  return todayStr().slice(0, 7);
}

/**
 * The earliest day somebody can file their own leave or work from home for, by
 * calendar date: the 1st of the current month for leave, and of the previous
 * month for WFH (monthsBack = 1). Mirrors the backend's earliestFilingDate
 * (utils/calendar.js), which is what actually enforces it.
 */
export function earliestFilingDate(monthsBack = 0): string {
  const [y, m] = calendarTodayStr().slice(0, 7).split("-").map(Number);
  return toDateStr(new Date(y ?? 1970, (m ?? 1) - 1 - monthsBack, 1));
}

export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split("-").map(Number);
  const date = new Date(y ?? 1970, (m ?? 1) - 1 + delta, 1);
  return toDateStr(date).slice(0, 7);
}

/**
 * Working-day count for a leave span — Sundays, 2nd/4th Saturdays, and fixed
 * holidays inside the range don't count, matching the backend's
 * workingDaysInclusive exactly. Used only for the live preview while someone
 * is still filling in an apply-for-leave form; every request the API returns
 * already carries its real `.days`, computed server-side.
 */
export function countWorkingDays(start: string, end: string, holidaySet?: Set<string>): number {
  return eachDateInRange(start, end).filter((d) => !isRestDay(d, holidaySet)).length;
}

export function holidayMap(holidays: Holiday[] | undefined, onlyFixed = true): Map<string, string> {
  const map = new Map<string, string>();
  for (const holiday of holidays ?? []) {
    // Only fixed holidays are org-wide days off. Optional ones are an
    // individual leave application, never a blanket closure.
    if (onlyFixed && holiday.category && holiday.category !== "fixed") continue;
    map.set(holiday.date, holiday.name);
  }
  return map;
}

/** Turns a MM-DD recurring date (birthday, joining date) into this year's date. */
export function nextAnniversary(dateStr: string, from = new Date()): Date {
  const source = parseDateStr(dateStr);
  const candidate = new Date(from.getFullYear(), source.getMonth(), source.getDate());
  if (candidate < new Date(from.getFullYear(), from.getMonth(), from.getDate())) {
    candidate.setFullYear(from.getFullYear() + 1);
  }
  return candidate;
}

export function daysUntil(date: Date, from = new Date()): number {
  const a = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime();
  const b = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  return Math.round((b - a) / 86_400_000);
}

/** Whole years completed, used for tenure and age-independent work anniversaries. */
export function yearsSince(dateStr: string, at = new Date()): number {
  const start = parseDateStr(dateStr);
  let years = at.getFullYear() - start.getFullYear();
  const beforeAnniversary =
    at.getMonth() < start.getMonth() ||
    (at.getMonth() === start.getMonth() && at.getDate() < start.getDate());
  if (beforeAnniversary) years -= 1;
  return Math.max(0, years);
}
